import { NgTemplateOutlet } from "@angular/common";
import { Component, Input, input, type OnChanges, type OnInit, output, type SimpleChanges } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Select, TextInput, Toggle, TooltipAnchor } from "@ui";
import type { ColumnMeta } from "@pienso/ingest-types/corpus.interface";
import type { ElasticFieldProps } from "@pienso/ingest-types/elastic.interface";
import type { Mapping } from "@pienso/ingest-types/pif.interface";

export type ColumnTypeMapping = Record<string, { id: string; name: string }[]>;

type GroupMapping = Pick<Mapping, "groupCol" | "sequenceCol" | "analyzeCol" | "analyzeQuery">;

interface ColumnOption {
  id: string;
  name: string;
}

@Component({
  selector: "app-mapping",
  templateUrl: "./mapping.component.html",
  styleUrls: ["./mapping.component.scss"],
  imports: [NgTemplateOutlet, FormsModule, Select, TextInput, Toggle, TooltipAnchor],
})
export class MappingComponent implements OnInit, OnChanges {
  @Input() mapping: Mapping | undefined;

  readonly columnMeta = input.required<ColumnMeta>();

  @Input() editable = true;

  readonly columnProps = input.required<ElasticFieldProps>();

  readonly mappingChange = output<Mapping>();

  typeMapping: ColumnTypeMapping | undefined; // derived from columnMeta

  showGroup = false;
  groupMapping: GroupMapping = {
    groupCol: "",
    sequenceCol: "",
    analyzeCol: "",
    analyzeQuery: "",
  };

  ngOnInit() {
    this.parseColumns();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.parseColumns();
    // Toggle the group slider on if the first mapping update has group fields
    if (
      changes["mapping"]["previousValue"] === null &&
      (!!this.mapping?.groupCol || !!this.mapping?.sequenceCol || !!this.mapping?.analyzeCol)
    ) {
      this.showGroup = true;
    }
  }

  /** Create a mapping of column types to column info in the corpus order */
  private parseColumns() {
    const columnMeta = this.columnMeta();
    if (!columnMeta || !this.columnProps() || !this.mapping) {
      return;
    }
    const meta: ColumnMeta = columnMeta;
    this.typeMapping = {};
    this.typeMapping["long"] = [{ id: "__rowNumber__", name: "Row number" }];
    this.typeMapping["all"] = [{ id: "__rowNumber__", name: "Row number" }];
    this.typeMapping["labels"] = [];
    meta.columnOrder.forEach((id) => {
      const type = this.columnProps()[id].type;
      const cols = this.typeMapping![type] || [];
      this.typeMapping![type] = cols;
      cols.push({ id, name: meta.columns[id].name });
      this.typeMapping!["all"].push({ id, name: meta.columns[id].name });
      this.typeMapping!["labels"].push({ id, name: meta.columns[id].name });
    });
    if (!this.mapping["idCol"]) {
      this.mapping["idCol"] = "__rowNumber__";
    }
  }

  /** Every column that qualifies for one of the given types, in corpus order and de-duplicated. */
  optionsFor(types: string[]): ColumnOption[] {
    if (!this.typeMapping) {
      return [];
    }
    const seen = new Set<string>();
    const result: ColumnOption[] = [];
    for (const type of types) {
      for (const col of this.typeMapping[type] ?? []) {
        if (!seen.has(col.id)) {
          seen.add(col.id);
          result.push(col);
        }
      }
    }
    return result;
  }

  selectedOption(colId: keyof Mapping, types: string[]): ColumnOption | null {
    const value = this.getMapping(colId);
    if (!value) {
      return null;
    }
    return this.optionsFor(types).find((option) => option.id === value) ?? null;
  }

  onOptionChange(colId: keyof Mapping, option: ColumnOption | null) {
    this.mappingChanged(colId, option?.id ?? "");
  }

  mappingChanged(prop: keyof Mapping, colId: string) {
    if (!this.mapping) {
      return;
    }
    this.mapping[prop] = colId && colId.trim().length ? colId : undefined;
    if (prop === "analyzeCol" && colId === "") {
      this.mapping.analyzeQuery = "";
    }
    this.mappingChange.emit(this.mapping);
  }

  columnName(mappingId: keyof Mapping) {
    if (mappingId === "idCol") {
      return "Row number";
    }
    if (!this.mapping || !this.mapping[mappingId]) {
      return "";
    }
    return this.columnMeta().columns[this.mapping[mappingId]].name;
  }

  setShowGroup(show: boolean) {
    if (!this.mapping) {
      return;
    }
    if (show) {
      this.mapping = {
        ...this.mapping,
        ...this.groupMapping,
      };
    } else {
      this.groupMapping = {
        groupCol: this.mapping.groupCol,
        sequenceCol: this.mapping.sequenceCol,
        analyzeCol: this.mapping.analyzeCol,
        analyzeQuery: this.mapping.analyzeQuery,
      };
      this.mapping.groupCol = "";
      this.mapping.sequenceCol = "";
      this.mapping.analyzeCol = "";
      this.mapping.analyzeQuery = "";
    }
    this.showGroup = show;
    this.mappingChange.emit(this.mapping);
  }

  getMapping(prop: string): string | null | undefined {
    return this.mapping?.[prop as keyof Mapping];
  }

  // export interface Mapping {
  //   idCol?: string;
  //   titleCol?: string;
  //   tagsCol?: string;
  //   authorCol?: string;
  //   publisherCol?: string;
  //   monthCol?: string;
  //   dayCol?: string;
  //   yearCol?: string;
  //   contentCol: string;
  // }
}
